import { useEffect, useState } from 'react';
import {
  Users,
  Trophy,
  Package,
  TrendingUp,
  Activity,
  Zap,
  UserCheck,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';

const StatsCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </div>
      <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-7 h-7" />
      </div>
    </div>
  </div>
);

const RecentActivityItem = ({ activity }) => {
  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-700';
      case 'update': return 'bg-blue-100 text-blue-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'ban': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="flex items-center gap-4 py-3 border-b last:border-0">
      <div className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(activity.action_type)}`}>
        {activity.action_type}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">
          {activity.entity_type} #{activity.entity_id}
        </p>
        <p className="text-xs text-gray-500">
          by {activity.admin_username || 'Admin'}
        </p>
      </div>
      <span className="text-xs text-gray-400">
        {new Date(activity.created_at).toLocaleString()}
      </span>
    </div>
  );
};

const AdminDashboard = () => {
  const { dashboard, loading, error, fetchDashboard, fetchActivityLog, activityLog } = useAdminStore();
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchDashboard(), fetchActivityLog({ limit: 10 })]);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Dashboard Overview</h2>
          <p className="text-gray-500">Monitor your platform's key metrics</p>
        </div>
        <button
          onClick={loadData}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={stats.total_users || 0}
          subValue={`${stats.verified_users || 0} verified`}
          icon={Users}
          color="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="Active Users (7d)"
          value={stats.active_users_7d || 0}
          icon={UserCheck}
          color="bg-green-100 text-green-600"
        />
        <StatsCard
          title="Challenges"
          value={stats.total_challenges || 0}
          subValue={`${stats.active_challenges || 0} active`}
          icon={Trophy}
          color="bg-yellow-100 text-yellow-600"
        />
        <StatsCard
          title="Components"
          value={stats.total_components || 0}
          icon={Package}
          color="bg-purple-100 text-purple-600"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Total Submissions"
          value={stats.total_submissions || 0}
          subValue={`Avg score: ${Math.round(stats.avg_submission_score || 0)}`}
          icon={Activity}
          color="bg-indigo-100 text-indigo-600"
        />
        <StatsCard
          title="Design Points Earned"
          value={(stats.total_dp_earned || 0).toLocaleString()}
          icon={Zap}
          color="bg-orange-100 text-orange-600"
        />
        <StatsCard
          title="New Users (30d)"
          value={stats.new_users_30d || 0}
          icon={TrendingUp}
          color="bg-teal-100 text-teal-600"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h3 className="font-semibold text-gray-800">Recent Admin Activity</h3>
          </div>
          <div className="px-6 py-2 max-h-80 overflow-y-auto">
            {activityLog?.length > 0 ? (
              activityLog.map((activity) => (
                <RecentActivityItem key={activity.id} activity={activity} />
              ))
            ) : (
              <p className="py-4 text-center text-gray-500">No recent activity</p>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h3 className="font-semibold text-gray-800">System Health</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Database Status</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">Healthy</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Email Service</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">Active</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">API Response Time</span>
              <span className="text-gray-900 font-medium">~120ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Banned Users</span>
              <span className="text-gray-900 font-medium">{stats.banned_users || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
